import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import ExportPanel from "./ExportPanel";
import StudioImprovePanel from "./improve/StudioImprovePanel";
import StudioDeployReadinessPanel from "./StudioDeployReadinessPanel";
import type { StudioDeploymentDecision } from "../../lib/studio/studioDeploymentState";
import type { StudioImproveLoopReadModel } from "../../lib/studio/studioImproveReadModel";
import type { StudioPremiumDemoModel } from "../../lib/studio/studioPremiumDemo";
import type { VetCanStudioStepId } from "../../lib/studio/studioReadModel";
import {
  getStudioWorkspaceVoicePersona,
  type StudioWorkspaceApprovalSelectionKey,
  STUDIO_HUMANIZATION_TOGGLE_DEFINITIONS,
  STUDIO_READINESS_SELECTION_DEFINITIONS,
  STUDIO_WORKSPACE_VOICE_PERSONAS,
  STUDIO_WORKSPACE_PREVIEW_CHANNELS,
  getStudioWorkspacePreviewChannelLabel,
  type StudioHumanizationToggleKey,
  type StudioWorkspaceReadinessSelectionKey,
  type StudioWorkspaceState,
  type StudioWorkspaceVoicePersonaId,
} from "../../lib/studio/studioWorkspaceState";

function panel(title: string, eyebrow: string, children: ReactNode) {
  return (
    <section
      style={{
        marginTop: "1.2rem",
        borderRadius: 28,
        border: "1px solid rgba(54, 44, 35, 0.08)",
        background:
          "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
        padding: "1.15rem",
      }}
    >
      <div
        style={{
          fontSize: "0.74rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#7b726a",
          fontWeight: 700,
        }}
      >
        {eyebrow}
      </div>
      <h3
        style={{
          margin: "0.45rem 0 0",
          fontSize: "clamp(1.4rem, 2.3vw, 1.9rem)",
          lineHeight: 1.1,
          fontFamily: "Georgia, 'Times New Roman', serif",
          color: "#26221d",
        }}
      >
        {title}
      </h3>
      <div style={{ marginTop: "0.95rem" }}>{children}</div>
    </section>
  );
}

function infoCard(label: string, value: string, detail?: string) {
  return (
    <div
      style={{
        borderRadius: 22,
        border: "1px solid rgba(54, 44, 35, 0.08)",
        background:
          "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
        padding: "1rem",
      }}
    >
      <div
        style={{
          fontSize: "0.72rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#85796e",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: "0.4rem",
          color: "#26221d",
          fontWeight: 700,
          lineHeight: 1.5,
        }}
      >
        {value}
      </div>
      {detail ? (
        <p style={{ margin: "0.42rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
          {detail}
        </p>
      ) : null}
    </div>
  );
}

function capabilityList(
  title: string,
  items: Array<{ label: string; reason: string; detected?: boolean }>
) {
  return (
    <section
      style={{
        borderRadius: 22,
        border: "1px solid rgba(54, 44, 35, 0.08)",
        background:
          "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
        padding: "1rem",
      }}
    >
      <div
        style={{
          fontSize: "0.74rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#85796e",
          fontWeight: 700,
        }}
      >
        {title}
      </div>
      <div style={{ marginTop: "0.8rem", display: "grid", gap: "0.75rem" }}>
        {items.map((item) => (
          <div
            key={`${title}_${item.label}`}
            style={{
              borderRadius: 18,
              border: "1px solid rgba(54, 44, 35, 0.08)",
              background: item.detected ? "rgba(255, 247, 244, 0.98)" : "#fbf8f4",
              padding: "0.85rem 0.95rem",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "0.75rem",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontWeight: 700, color: "#26221d" }}>{item.label}</div>
              {item.detected ? (
                <span
                  style={{
                    borderRadius: 999,
                    padding: "0.24rem 0.54rem",
                    background: "rgba(162, 62, 62, 0.12)",
                    color: "#812d2d",
                    fontSize: "0.74rem",
                    fontWeight: 700,
                  }}
                >
                  detected
                </span>
              ) : null}
            </div>
            <p style={{ margin: "0.38rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
              {item.reason}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function draftControlSection(title: string, detail: string, children: ReactNode) {
  return (
    <section
      style={{
        marginTop: "1rem",
        borderRadius: 22,
        border: "1px solid rgba(41, 84, 103, 0.14)",
        background:
          "linear-gradient(180deg, rgba(240, 248, 250, 0.9), rgba(248, 251, 252, 0.98))",
        padding: "1rem",
      }}
    >
      <div
        style={{
          fontSize: "0.74rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#5b7884",
          fontWeight: 700,
        }}
      >
        {title}
      </div>
      <p style={{ margin: "0.55rem 0 0", color: "#39535f", lineHeight: 1.65 }}>
        {detail}
      </p>
      <div style={{ marginTop: "0.95rem" }}>{children}</div>
    </section>
  );
}

export default function StudioPremiumStepContent(props: {
  activeStepId: VetCanStudioStepId;
  operatorDescription: string;
  onOperatorDescriptionChange: (value: string) => void;
  onVoicePersonaChange: (value: StudioWorkspaceVoicePersonaId) => void;
  onPreviewChannelChange: (
    value: StudioWorkspaceState["preview"]["selectedChannel"]
  ) => void;
  onHumanizationToggleChange: (key: StudioHumanizationToggleKey, value: boolean) => void;
  onDeploymentNotesChange: (value: string) => void;
  onReadinessSelectionChange: (
    key: StudioWorkspaceReadinessSelectionKey,
    value: boolean
  ) => void;
  onApprovalSelectionChange: (
    key: StudioWorkspaceApprovalSelectionKey,
    value: boolean
  ) => void;
  onRecordApproval: () => void;
  onClearApproval: () => void;
  onDeploymentDecisionChange?: (decision: StudioDeploymentDecision) => void;
  deploymentDecision: StudioDeploymentDecision | null;
  premiumDemo: StudioPremiumDemoModel;
  improveLoopModel: StudioImproveLoopReadModel;
  workspaceState: StudioWorkspaceState;
}) {
  const selectedPersona = getStudioWorkspaceVoicePersona(
    props.workspaceState.voicePersona.selectedPersonaId
  );

  switch (props.activeStepId) {
    case "describe_assistant":
      return panel("Generated studio brief", "Premium demo builder", (
        <>
          <label style={{ display: "grid", gap: "0.45rem" }}>
            <span style={{ fontSize: "0.84rem", fontWeight: 700, color: "#5a524b" }}>
              Operator description
            </span>
            <textarea
              aria-label="Studio operator description"
              value={props.operatorDescription}
              onChange={(event) => props.onOperatorDescriptionChange(event.target.value)}
              rows={5}
              style={{
                width: "100%",
                boxSizing: "border-box",
                borderRadius: 18,
                border: "1px solid rgba(54, 44, 35, 0.12)",
                padding: "0.9rem 1rem",
                font: "inherit",
                resize: "vertical",
                background: "#fffdfa",
              }}
            />
          </label>

          <p style={{ margin: "0.8rem 0 0", color: "#5b534b", lineHeight: 1.65 }}>
            Changes stay local to the install-week draft. The brief is deterministic and
            updates the persona, capability, preview, and deploy-readiness summaries
            in this shell only.
          </p>

          <div
            style={{
              marginTop: "1rem",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "0.85rem",
            }}
          >
            {infoCard("Assistant name", props.premiumDemo.brief.assistantName)}
            {infoCard("Starter tone", props.premiumDemo.brief.starterTone)}
            {infoCard("Mode", props.premiumDemo.brief.mode)}
          </div>

          <div
            style={{
              marginTop: "1rem",
              borderRadius: 22,
              border: "1px solid rgba(41, 84, 103, 0.14)",
              background:
                "linear-gradient(180deg, rgba(240, 248, 250, 0.9), rgba(248, 251, 252, 0.98))",
              padding: "1rem",
            }}
          >
            <div
              style={{
                fontSize: "0.74rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#5b7884",
                fontWeight: 700,
              }}
            >
              Starter prompt
            </div>
            <p style={{ margin: "0.7rem 0 0", color: "#39535f", lineHeight: 1.7 }}>
              {props.premiumDemo.brief.starterPrompt}
            </p>
          </div>

          <div style={{ marginTop: "1rem", display: "grid", gap: "0.7rem" }}>
            {props.premiumDemo.brief.readinessNotes.map((note) => (
              <div
                key={note}
                style={{
                  borderRadius: 18,
                  padding: "0.85rem 0.95rem",
                  background: "#fbf7f1",
                  border: "1px solid rgba(54, 44, 35, 0.08)",
                  color: "#403931",
                  lineHeight: 1.55,
                }}
              >
                {note}
              </div>
            ))}
          </div>
        </>
      ));

    case "persona":
      return panel("Persona and voice summary", "Premium demo persona", (
        <>
          {draftControlSection(
            "Draft control",
            "Select the preview-only voice persona for this Studio draft. The change stays local, persists in this browser, and updates Studio summaries and export packages. Supported preview-catalog mappings can influence the internal preview lane only; unmapped personas fall back to the current server preview binding.",
            <label style={{ display: "grid", gap: "0.45rem" }}>
              <span style={{ fontSize: "0.84rem", fontWeight: 700, color: "#39535f" }}>
                Preview voice persona
              </span>
              <select
                aria-label="Studio voice persona"
                value={props.workspaceState.voicePersona.selectedPersonaId}
                onChange={(event) =>
                  props.onVoicePersonaChange(
                    event.target.value as StudioWorkspaceVoicePersonaId
                  )
                }
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  borderRadius: 18,
                  border: "1px solid rgba(41, 84, 103, 0.18)",
                  padding: "0.85rem 0.95rem",
                  font: "inherit",
                  background: "#fffdfa",
                }}
              >
                {STUDIO_WORKSPACE_VOICE_PERSONAS.map((persona) => (
                  <option key={persona.id} value={persona.id}>
                    {persona.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div
            style={{
              marginTop: "1rem",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "0.85rem",
            }}
          >
            {infoCard(
              "Persona",
              props.premiumDemo.voiceSummary.personaLabel,
              `${props.premiumDemo.voiceSummary.personaSummary} Best use: ${selectedPersona.bestUse}`
            )}
            {infoCard(
              "Workspace preview mode",
              props.premiumDemo.voiceSummary.selectedPreviewChannelLabel
            )}
            {infoCard("Provider", props.premiumDemo.voiceSummary.providerMode)}
            {infoCard("Voice model", props.premiumDemo.voiceSummary.modelLabel)}
            {infoCard(
              "Preview runtime mapping",
              selectedPersona.previewCatalogEntryLabel || "Server env fallback",
              selectedPersona.previewCatalogEntryLabel
                ? "This selected persona has a preview-only catalog entry."
                : "This selected persona stays draft-safe, but preview renders fall back to the current server preview binding."
            )}
            {infoCard("Routing note", props.premiumDemo.voiceSummary.routingNote)}
          </div>
        </>
      ));

    case "capabilities":
      return panel("Capability choreography", "Premium demo capabilities", (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 0.9fr)",
            gap: "1rem",
            alignItems: "start",
          }}
        >
          {capabilityList(
            "Recommended capabilities",
            props.premiumDemo.brief.recommendedCapabilities.map((capability) => ({
              label: capability.label,
              reason: capability.reason,
            }))
          )}
          {capabilityList(
            "Blocked capability signals",
            props.premiumDemo.brief.blockedCapabilities.map((capability) => ({
              label: capability.label,
              reason: capability.reason,
              detected: capability.detectedFromInput,
            }))
          )}
        </div>
      ));

    case "booking_behavior":
      return panel("Routing truth summary", "Public-only posture", (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "0.85rem",
          }}
        >
          {props.premiumDemo.deployReadiness.routingSummary.map((line) => (
            <div key={line}>{infoCard("Routing summary", line)}</div>
          ))}
        </div>
      ));

    case "preview":
      return (
        <>
          {panel("Preview, test, and iterate", "Guided choreography", (
            <>
              <p style={{ margin: "0 0 1rem", color: "#5b534b", lineHeight: 1.65 }}>
                Showcase presets are presentation-quality entry paths only. They help
                operators and founders rehearse the right story without bypassing blocker
                truth or launch-control boundaries.
              </p>

              {draftControlSection(
                "Draft controls",
                "Pick the Studio workspace preview mode and humanization emphasis for this draft only. These controls update local preview prep and export summaries, not live routing or scenario-lane policy.",
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                      gap: "0.85rem",
                    }}
                  >
                    <label style={{ display: "grid", gap: "0.45rem" }}>
                      <span
                        style={{ fontSize: "0.84rem", fontWeight: 700, color: "#39535f" }}
                      >
                        Studio workspace preview mode
                      </span>
                      <select
                        aria-label="Studio workspace preview mode"
                        value={props.workspaceState.preview.selectedChannel}
                        onChange={(event) =>
                          props.onPreviewChannelChange(
                            event.target.value as StudioWorkspaceState["preview"]["selectedChannel"]
                          )
                        }
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          borderRadius: 18,
                          border: "1px solid rgba(41, 84, 103, 0.18)",
                          padding: "0.85rem 0.95rem",
                          font: "inherit",
                          background: "#fffdfa",
                        }}
                      >
                        {STUDIO_WORKSPACE_PREVIEW_CHANNELS.map((channel) => (
                          <option key={channel} value={channel}>
                            {getStudioWorkspacePreviewChannelLabel(channel)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div
                      style={{
                        borderRadius: 18,
                        border: "1px solid rgba(41, 84, 103, 0.12)",
                        background: "rgba(255, 255, 255, 0.85)",
                        padding: "0.9rem 0.95rem",
                        color: "#39535f",
                        lineHeight: 1.6,
                      }}
                    >
                      Studio uses this channel label for preview prep and export. The
                      actual execution lane stays restricted to the approved internal
                      preview set only.
                    </div>
                  </div>

                  <div style={{ marginTop: "1rem" }}>
                    <div
                      style={{
                        fontSize: "0.84rem",
                        fontWeight: 700,
                        color: "#39535f",
                      }}
                    >
                      Humanization toggles
                    </div>
                    <div
                      style={{
                        marginTop: "0.7rem",
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: "0.75rem",
                      }}
                    >
                      {STUDIO_HUMANIZATION_TOGGLE_DEFINITIONS.map((toggle) => (
                        <label
                          key={toggle.key}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "auto minmax(0, 1fr)",
                            gap: "0.75rem",
                            alignItems: "start",
                            borderRadius: 18,
                            border: "1px solid rgba(41, 84, 103, 0.12)",
                            background: "rgba(255, 255, 255, 0.85)",
                            padding: "0.85rem 0.95rem",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            aria-label={`Studio humanization ${toggle.label.toLowerCase()}`}
                            checked={props.workspaceState.humanization[toggle.key]}
                            onChange={(event) =>
                              props.onHumanizationToggleChange(toggle.key, event.target.checked)
                            }
                            style={{ marginTop: "0.15rem" }}
                          />
                          <span>
                            <span
                              style={{
                                display: "block",
                                fontWeight: 700,
                                color: "#26221d",
                              }}
                            >
                              {toggle.label}
                            </span>
                            <span
                              style={{
                                display: "block",
                                marginTop: "0.3rem",
                                color: "#5b534b",
                                lineHeight: 1.55,
                              }}
                            >
                              {toggle.detail}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "0.85rem",
                }}
              >
                {infoCard("Preview posture", props.premiumDemo.previewCheckpoint.statusLabel)}
                {infoCard(
                  "Workspace preview mode",
                  props.premiumDemo.voiceSummary.selectedPreviewChannelLabel
                )}
                {infoCard(
                  "Allowed scenario channels",
                  props.premiumDemo.voiceSummary.channelLabels.join(", ")
                )}
                {infoCard(
                  "Improve loop",
                  `${props.premiumDemo.improveLoopSummary.reviewableSuggestionCount} reviewable / ${props.premiumDemo.improveLoopSummary.blockedSuggestionCount} blocked`
                )}
                {infoCard(
                  "Humanization focus",
                  props.premiumDemo.voiceSummary.humanizationLabels.join(", ") ||
                    "Manual QA only"
                )}
              </div>

              <p style={{ margin: "1rem 0 0", color: "#5b534b", lineHeight: 1.7 }}>
                {props.premiumDemo.previewCheckpoint.summary}
              </p>

              <ul
                style={{
                  margin: "0.85rem 0 0",
                  paddingLeft: "1.1rem",
                  color: "#5b534b",
                  lineHeight: 1.7,
                }}
              >
                {props.premiumDemo.previewCheckpoint.blockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>

              <div
                style={{
                  marginTop: "0.95rem",
                  display: "flex",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Link
                  to={props.premiumDemo.previewCheckpoint.previewRoute}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 999,
                    padding: "0.78rem 1rem",
                    textDecoration: "none",
                    background: "linear-gradient(135deg, #173847, #285466)",
                    color: "#f7f4ee",
                    fontWeight: 700,
                  }}
                >
                  Open Preview & Test Center
                </Link>
                <span style={{ color: "#5b534b", lineHeight: 1.6 }}>
                  {props.premiumDemo.previewCheckpoint.suggestedNextStep}
                </span>
              </div>
            </>
          ))}
          <StudioImprovePanel model={props.improveLoopModel} />
        </>
      );

    case "deploy_readiness":
      return (
        <>
          {panel("Draft deployment controls", "Draft-only operator state", (
            <>
              <label style={{ display: "grid", gap: "0.45rem" }}>
                <span style={{ fontSize: "0.84rem", fontWeight: 700, color: "#5a524b" }}>
                  Deployment notes
                </span>
                <textarea
                  aria-label="Studio deployment notes"
                  value={props.workspaceState.deployment.notes}
                  onChange={(event) => props.onDeploymentNotesChange(event.target.value)}
                  rows={4}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    borderRadius: 18,
                    border: "1px solid rgba(54, 44, 35, 0.12)",
                    padding: "0.9rem 1rem",
                    font: "inherit",
                    resize: "vertical",
                    background: "#fffdfa",
                  }}
                />
              </label>

              <p style={{ margin: "0.8rem 0 0", color: "#5b534b", lineHeight: 1.65 }}>
                Notes stay in the local Studio draft and the audit-safe export package
                only. They do not trigger live deployment, Twilio changes, or webhook
                cutover behavior.
              </p>

              <div
                style={{
                  marginTop: "1rem",
                  borderRadius: 22,
                  border: "1px solid rgba(41, 84, 103, 0.14)",
                  background:
                    "linear-gradient(180deg, rgba(240, 248, 250, 0.9), rgba(248, 251, 252, 0.98))",
                  padding: "1rem",
                }}
              >
                <div
                  style={{
                    fontSize: "0.74rem",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "#5b7884",
                    fontWeight: 700,
                  }}
                >
                  Readiness acknowledgements
                </div>
                <p style={{ margin: "0.55rem 0 0", color: "#39535f", lineHeight: 1.65 }}>
                  {
                    STUDIO_READINESS_SELECTION_DEFINITIONS.filter(
                      (selection) =>
                        props.workspaceState.deployment.readinessSelections[selection.key]
                    ).length
                  }{" "}
                  of {STUDIO_READINESS_SELECTION_DEFINITIONS.length} draft
                  acknowledgements complete.
                </p>
                <div
                  style={{
                    marginTop: "0.85rem",
                    display: "grid",
                    gap: "0.75rem",
                  }}
                >
                  {STUDIO_READINESS_SELECTION_DEFINITIONS.map((selection) => (
                    <label
                      key={selection.key}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "auto minmax(0, 1fr)",
                        gap: "0.75rem",
                        alignItems: "start",
                        borderRadius: 18,
                        border: "1px solid rgba(41, 84, 103, 0.12)",
                        background: "rgba(255, 255, 255, 0.85)",
                        padding: "0.85rem 0.95rem",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={`Studio readiness ${selection.label.toLowerCase()}`}
                        checked={
                          props.workspaceState.deployment.readinessSelections[selection.key]
                        }
                        onChange={(event) =>
                          props.onReadinessSelectionChange(selection.key, event.target.checked)
                        }
                        style={{ marginTop: "0.15rem" }}
                      />
                      <span>
                        <span
                          style={{
                            display: "block",
                            fontWeight: 700,
                            color: "#26221d",
                          }}
                        >
                          {selection.label}
                        </span>
                        <span
                          style={{
                            display: "block",
                            marginTop: "0.3rem",
                            color: "#5b534b",
                            lineHeight: 1.55,
                          }}
                        >
                          {selection.detail}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          ))}
          <StudioDeployReadinessPanel
            readiness={props.premiumDemo.deployReadiness}
            workspaceState={props.workspaceState}
            onReadinessSelectionChange={props.onReadinessSelectionChange}
            onApprovalSelectionChange={props.onApprovalSelectionChange}
            onRecordApproval={props.onRecordApproval}
            onClearApproval={props.onClearApproval}
            onDecisionChange={props.onDeploymentDecisionChange}
          />
          <ExportPanel
            workspaceState={props.workspaceState}
            premiumDemo={props.premiumDemo}
            improveLoopModel={props.improveLoopModel}
            deploymentDecision={props.deploymentDecision}
          />
        </>
      );

    default:
      return null;
  }
}
